// @etp/auth-shared: paquete compartido worker↔dashboard (ADR 0002).
import { canPlatformDo } from "@etp/auth-shared";
import type { PermissionsMap, PlatformScope } from "./platform-api";

export { canPlatformDo };

/**
 * ¿Puede el actor leer el audit log de UNA organización puntual
 * (`GET /api/platform/audit?orgId=`)? Réplica cliente del gate compuesto del
 * worker — `platform-router.ts`, handler `GET /audit` — usada por
 * `TenantAuditTab` (TICKET-010) para decidir si la tab "Actividad" de la
 * ficha de tenant se muestra.
 *
 * DELEGACIÓN EN CADENA (decisión producto 2026-08-14, ver
 * `platformOrgStatement.audit` en `@etp/auth-shared`): el verbo `audit:read`
 * NO se hereda por posición jerárquica, se delega explícitamente. Por eso
 * "ámbito global por nombre" (`myScope.mode === "global"`, p. ej. un
 * studio-admin base) NO alcanza por sí solo — hace falta el verbo.
 *
 *   RAMA 1 — verbo GLOBAL sobre `myPermissions` (superAdmin, o studio-admin
 *   promovido por superAdmin a un rol dinámico global que lo incluya).
 *
 *   RAMA 2 — verbo POR-CLIENTE: solo si el actor es `scoped` y tiene una
 *   asignación VIGENTE para `orgId` cuyo `permissionsSnapshot` (ya resuelto
 *   por `GET /api/platform/me`, sin ronda adicional de requests) incluye el
 *   verbo. `permissionsSnapshot: null` (rol por-cliente corrupto/borrado) se
 *   trata como "sin poder", igual que en el resto de la consola.
 *
 * Puramente informativo del lado cliente — el server SIEMPRE re-valida esto
 * mismo en cada request; si el permiso se revoca a mitad de sesión, el 403
 * tardío lo absorbe `TenantAuditTab` como estado esperado, no como error.
 */
export function canReadTenantAudit(
  myPermissions: PermissionsMap,
  myScope: PlatformScope,
  orgId: string,
): boolean {
  if (canPlatformDo(myPermissions, { audit: ["read"] })) return true;

  if (myScope.mode !== "scoped") return false;
  const assignment = myScope.assignments.find((a) => a.orgId === orgId);
  if (!assignment?.permissionsSnapshot) return false;
  return canPlatformDo(assignment.permissionsSnapshot, { audit: ["read"] });
}

/**
 * platformFetch — wrapper para llamadas a /api/platform/*.
 *
 * Agrega credentials: "include" y el header X-Requested-With requerido
 * por el middleware requireCsrfDefense del worker en toda mutación
 * (POST/PUT/PATCH/DELETE). GETs no lo requieren pero también llevan
 * credentials.
 *
 * Referencia: HANDOFF_auth-client-plugins.md §6.
 *
 * NOTA: este módulo es client-only — no importarlo en código SSR/worker.
 * La consola de plataforma (Nivel C) consumirá estas primitivas;
 * acá solo se declaran para evitar duplicación futura.
 */
export async function platformFetch(path: string, init: RequestInit = {}): Promise<Response> {
  const method = (init.method ?? "GET").toUpperCase();
  const headers = new Headers(init.headers);

  if (["POST", "PUT", "PATCH", "DELETE"].includes(method)) {
    headers.set("X-Requested-With", "portal-dashboard");
    if (!headers.has("Content-Type") && init.body) {
      headers.set("Content-Type", "application/json");
    }
  }

  return fetch(`${import.meta.env.VITE_API_URL ?? "http://localhost:6787"}${path}`, {
    ...init,
    credentials: "include",
    headers,
  });
}
